import { Seo } from '../components/academy/Seo';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, AlertTriangle, Download } from 'lucide-react';

const sampleBooking = {
  carYear: '2019',
  carMake: 'BMW',
  carModel: 'M340i xDrive',
  vin: 'WBA5U9C05LFH12345',
};

const sampleInspection = {
  createdAt: new Date().toISOString(),
  photos: [
    'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=1200&q=80',
  ],
  summary:
    'This 2019 BMW M340i xDrive presents in very good overall condition with no signs of prior structural or collision damage. The vehicle drives well with a strong-running engine and smooth-shifting transmission. A few minor cosmetic items and upcoming maintenance needs were noted below. Overall, this is a well-maintained example suitable for purchase, with the noted items factored into your ownership plan.',
  vehicleInfo: {
    mileage: '48,120',
    transmission: '8-Speed Automatic',
    interiorColor: 'Black Vernasca Leather',
    bodyColor: 'Portimao Blue Metallic',
    engine: '3.0L Turbocharged Inline-6',
    driveType: 'AWD (xDrive)',
    fuelType: 'Gasoline (Premium)',
  },
  exteriorChecks: {
    paint: { status: 'good' },
    bodyPanels: { status: 'good' },
    frontBumper: { status: 'issue', notes: 'Minor rock chips on leading edge; touch-up recommended.' },
    glassAndWindshield: { status: 'good' },
    lights: { status: 'good' },
    tires: { status: 'issue', notes: 'Front tires at ~4/32" tread; replacement recommended within 6 months.' },
    wheels: { status: 'good' },
  },
  interiorChecks: {
    seatsAndUpholstery: { status: 'good' },
    dashboardAndControls: { status: 'good' },
    infotainment: { status: 'good' },
    airConditioning: { status: 'good' },
    warningLights: { status: 'good' },
  },
  mechanicalChecks: {
    engine: { status: 'good' },
    transmission: { status: 'good' },
    brakes: { status: 'issue', notes: 'Rear brake pads at ~30% life; plan for replacement soon.' },
    suspension: { status: 'good' },
    exhaust: { status: 'good' },
    fluidsAndLeaks: { status: 'good' },
    battery: { status: 'good' },
  },
  roadTest: {
    acceleration: { status: 'good' },
    braking: { status: 'good' },
    steeringAndAlignment: { status: 'good' },
    noisesAndVibration: { status: 'good' },
  },
  inspectorNotes:
    'Vehicle history and VIN confirmed to match the title and registration. No evidence of airbag deployment or frame repair. Recommend addressing front tires and rear brakes as part of routine maintenance. A clean example overall.',
};

const SampleReport = () => {
  const navigate = useNavigate();

  const renderCheckItem = (label, check) => {
    if (!check) return null;
    return (
      <div className="border-b border-zinc-700 py-3" key={label}>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <p className="font-semibold text-zinc-900 uppercase text-sm">{label}</p>
            {check.notes && <p className="text-red-500 text-sm mt-1">{check.notes}</p>}
          </div>
          <div>
            {check.status === 'good' ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, checks) => {
    if (!checks) return null;
    return (
      <div className="mb-8">
        <div className="bg-zinc-800 px-6 py-3 mb-2">
          <h3 className="text-xl font-bold text-white uppercase tracking-wide">{title}</h3>
        </div>
        <div className="px-6">
          {Object.entries(checks).map(([key, check]) =>
            renderCheckItem(key.replace(/([A-Z])/g, ' $1').trim(), check)
          )}
        </div>
      </div>
    );
  };

  const b = sampleBooking;
  const inspection = sampleInspection;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="sample-report-page">
      <Seo siteName="CarLens" title="Sample Vehicle Inspection Report | CarLens" description="Explore a real CarLens Vehicle Decision Report — Confidence Score, findings, photo evidence, and buy/negotiate guidance from an actual inspection." path="/sample-report" />
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/')}
            data-testid="sample-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-white/5"
              onClick={() => window.print()}
              data-testid="sample-print-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download / Print
            </Button>
            <Button
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              onClick={() => navigate('/book')}
              data-testid="sample-book-btn"
            >
              Book Inspection Now
            </Button>
          </div>
        </div>
      </header>

      {/* Sample Banner */}
      <div className="bg-orange-500 text-white text-center py-2 text-sm font-semibold print:hidden">
        SAMPLE REPORT — This is an example of the report you'll receive after your inspection.
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-6 py-12 bg-white my-8 shadow-lg">
        <div className="text-center mb-8 border-b-4 border-zinc-900 pb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png"
            alt="CarLens"
            className="h-24 mx-auto mb-4"
          />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-zinc-900 mb-2" data-testid="sample-vehicle-title">
            {b.carYear} {b.carMake} {b.carModel}
          </h1>
          <p className="text-zinc-600">
            Date of Inspection: {new Date(inspection.createdAt).toLocaleDateString()}
          </p>
        </div>

        {inspection.photos && inspection.photos.length > 0 && (
          <div className="mb-8">
            <div className="border-4 border-orange-400 p-2">
              <img src={inspection.photos[0]} alt="Vehicle" className="w-full h-96 object-cover" />
            </div>
          </div>
        )}

        {inspection.summary && (
          <div className="mb-8 bg-zinc-100 p-6">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">SUMMARY</h2>
            <p className="text-zinc-800 leading-relaxed">{inspection.summary}</p>
          </div>
        )}

        {/* Vehicle Info Table */}
        <div className="mb-8">
          <div className="bg-zinc-800 px-6 py-3 mb-2">
            <h3 className="text-xl font-bold text-white uppercase tracking-wide">VEHICLE INFO</h3>
          </div>
          <table className="w-full border border-zinc-300">
            <tbody>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100 w-1/3">YEAR</td>
                <td className="px-6 py-2">{b.carYear}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">MAKE</td>
                <td className="px-6 py-2">{b.carMake}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">MODEL</td>
                <td className="px-6 py-2">{b.carModel}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">MILEAGE</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.mileage}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">TRANSMISSION</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.transmission}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">VIN NUMBER</td>
                <td className="px-6 py-2">{b.vin}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">INTERIOR COLOR</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.interiorColor}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">BODY COLOR</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.bodyColor}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">ENGINE</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.engine}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">DRIVE TYPE</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.driveType}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">FUEL TYPE</td>
                <td className="px-6 py-2">{inspection.vehicleInfo.fuelType}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {renderSection('EXTERIOR', inspection.exteriorChecks)}
        {renderSection('INTERIOR', inspection.interiorChecks)}
        {renderSection('MECHANICAL', inspection.mechanicalChecks)}
        {renderSection('ROAD TEST', inspection.roadTest)}

        {inspection.inspectorNotes && (
          <div className="mb-8 bg-zinc-100 p-6">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">INSPECTOR NOTES</h2>
            <p className="text-zinc-800 leading-relaxed">{inspection.inspectorNotes}</p>
          </div>
        )}

        <div className="text-center mt-12 pt-6 border-t border-zinc-300">
          <p className="text-zinc-600">CarLens Inspections</p>
          <p className="text-zinc-500 text-sm">Professional Pre-Purchase Vehicle Inspections</p>
        </div>
      </div>
    </div>
  );
};

export default SampleReport;
